import { useState } from 'react';
import { T, useTheme, dens, Card, Eyebrow, Ico } from '../../../design';
import { fmtCount } from '../../../utils';
import { ACCENT_SOFT } from '../primeStatsConfig';
import { StatHero, SplitBar, LegendDot } from './StatPrimitives';
import { ChartCard } from './DevelopmentChart';
import { ListCard, EmployeeRow, SalonRow } from './rows';
import { Avatar } from './Avatar';
import type { MeStats, SalonStats, ChainStats, SalonEmployeeRow } from '../../../types/primeStats.types';

function Stack({ children }: { children: React.ReactNode }) {
  const t = useTheme();
  return <div style={{ display: 'flex', flexDirection: 'column', gap: dens(t).gap }}>{children}</div>;
}

export function MeView({ data }: { data: MeStats }) {
  return (
    <Stack>
      <StatHero eyebrow="Mine PRIME-medlemmer" totals={data.totals} />
      <ChartCard title="Din udvikling" data={data.development}
        note="24 frosne måneder + indeværende måned (live). Medlemmer du ejer via dit telefonnummer." />
    </Stack>
  );
}

export function SalonView({ data, onEmployee }: { data: SalonStats; onEmployee: (row: SalonEmployeeRow) => void }) {
  const emps = data.byEmployee.filter((r) => !r.salonOwned).length;
  return (
    <Stack>
      <StatHero eyebrow="Salonens PRIME-medlemmer" totals={data.totals} />
      <ChartCard title="Salonens udvikling" data={data.development} />
      <ListCard title="Per medarbejder" count={emps}>
        {data.byEmployee.map((row) => (
          <EmployeeRow key={row.employeeId == null ? 'salon' : row.employeeId} row={row} salonTotal={data.totals.total}
            onClick={row.salonOwned ? null : () => onEmployee(row)} />
        ))}
      </ListCard>
    </Stack>
  );
}

export function EmployeeDetail({ row, salonName, salonTotal }: { row: SalonEmployeeRow; salonName: string; salonTotal: number }) {
  const t = useTheme();
  const d = dens(t);
  const pct = Math.round((row.total / Math.max(1, salonTotal)) * 100);
  return (
    <Stack>
      <StatHero eyebrow="Medarbejder" totals={{ total: row.total, primary: row.primary, family: row.family }} />
      <Card pad={d.pad}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={row.name} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.fg }}>{row.name}</div>
            <div style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600 }}>{row.employeeId != null ? `Park-ID ${row.employeeId} · ` : ''}{salonName}</div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 30, fontWeight: 800, color: t.accent, letterSpacing: '-0.02em' }}>{pct}%</span>
          <span style={{ fontSize: 12.5, color: T.fg2, fontWeight: 600 }}>af salonens {fmtCount(salonTotal)} medlemmer</span>
        </div>
        <div style={{ marginTop: 10 }}><SplitBar primary={row.primary} family={row.family} /></div>
        <div style={{ display: 'flex', gap: 18, marginTop: 12 }}>
          <LegendDot color={t.accent} label="Hovedmedlem" value={fmtCount(row.primary)} />
          <LegendDot color={ACCENT_SOFT} label="Familiemedlem" value={fmtCount(row.family)} />
        </div>
      </Card>
    </Stack>
  );
}

export function ChainView({ data, onSalon }: { data: ChainStats; onSalon: (salonId: number) => void }) {
  const t = useTheme();
  const d = dens(t);
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const sorted = [...data.salons].sort((a, b) => b.total - a.total);
  const max = sorted[0]?.total ?? 1;
  const rankOf = new Map<number, number>();
  sorted.forEach((s, i) => rankOf.set(s.id, i + 1));
  const needle = q.trim().toLowerCase();
  const filtered = needle ? sorted.filter((s) => `${s.name} ${s.city ?? ''}`.toLowerCase().includes(needle)) : sorted;
  return (
    <Stack>
      <StatHero eyebrow="PRIME i hele kæden" totals={data.totals} />
      <div style={{ display: 'flex', gap: d.gap }}>
        <Card pad={d.pad} style={{ flex: 1 }}>
          <Eyebrow>Aktive saloner</Eyebrow>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em', marginTop: 6 }}>{fmtCount(data.salons.length)}</div>
        </Card>
        <Card pad={d.pad} style={{ flex: 1 }}>
          <Eyebrow>Uden frisør/salon</Eyebrow>
          <div style={{ fontSize: 30, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em', marginTop: 6 }}>{fmtCount(data.unowned.total)}</div>
        </Card>
      </div>
      <ChartCard title="Kædens udvikling" data={data.development}
        note={`Inkl. ${fmtCount(data.unowned.total)} medlemmer uden ejer — alle dækkede medlemmer tæller med.`} />
      <ListCard title="Saloner" count={needle ? `${filtered.length}/${sorted.length}` : sorted.length}>
        <div style={{ padding: `2px ${d.pad}px 12px` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, height: 44, padding: '0 13px', borderRadius: 12, background: T.tertiary, border: `1.5px solid ${focus ? t.accent : T.border}`, boxShadow: focus ? `0 0 0 3px ${t.accent}1a` : 'none', transition: 'all 160ms ease' }}>
            <span style={{ color: focus ? t.accent : T.fg3, display: 'flex', flexShrink: 0 }}><Ico.search size={17} /></span>
            <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
              placeholder="Søg salon eller by…" aria-label="Søg salon eller by" autoComplete="off"
              style={{ border: 0, outline: 0, flex: 1, minWidth: 0, background: 'transparent', fontSize: 15, fontFamily: T.font, fontWeight: 600, color: T.fg }} />
            {q && <button onClick={() => setQ('')} aria-label="Ryd søgning" style={{ width: 26, height: 26, flexShrink: 0, borderRadius: 999, border: 0, background: T.track, color: T.fg2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Ico.x size={13} /></button>}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: '8px 16px 22px', textAlign: 'center', color: T.fg3, fontSize: 13, fontWeight: 600, borderTop: `1px solid ${T.borderLight}` }}>
            <div style={{ paddingTop: 18 }}>Ingen saloner fundet</div>
          </div>
        ) : filtered.map((s) => (
          <SalonRow key={s.id} salon={s} rank={rankOf.get(s.id) ?? 0} max={max} onClick={() => onSalon(s.id)} />
        ))}
      </ListCard>
    </Stack>
  );
}
